import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Wrench, AlertTriangle, Clock, Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { fetchMolds } from '@/lib/api/molds';
import { useAuth } from '@/contexts/AuthContext';
import { CreateWorkOrderDialog } from '@/components/work-orders/CreateWorkOrderDialog';
import { cn } from '@/lib/utils';
import type { Mold } from '@/types/maintenance';

function SectionHeader({ icon, label, count, color }: {
  icon: React.ReactNode;
  label: string;
  count: number;
  color: string;
}) {
  return (
    <div className={cn('flex items-center gap-2 px-3 py-2 border-b border-border/50', color)}>
      {icon}
      <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      <span className="ml-auto text-xs font-bold">{count}</span>
    </div>
  );
}

function MoldRow({
  mold,
  showShotsOverdue,
  onCreatePM,
}: {
  mold: Mold;
  showShotsOverdue?: boolean;
  onCreatePM?: (moldId: string) => void;
}) {
  return (
    <div className="flex items-center gap-1 px-3 py-2 hover:bg-muted/50 transition-colors group">
      <Link
        to={`/maintenance/molds/${mold.id}`}
        className="flex-1 min-w-0 flex items-center gap-2"
      >
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium truncate">{mold.mold_id}</p>
          {showShotsOverdue && mold.shots_until_pm != null && mold.shots_until_pm < 0 ? (
            <p className="text-[10px] text-[hsl(var(--status-red))] font-medium">
              {Math.abs(mold.shots_until_pm).toLocaleString()} shots over
            </p>
          ) : (
            <p className="text-[11px] text-muted-foreground truncate">{mold.name}</p>
          )}
        </div>
        <ArrowRight className="h-3 w-3 text-muted-foreground/40 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
      </Link>
      {onCreatePM && (
        <button
          onClick={() => onCreatePM(mold.id)}
          title="Create PM work order"
          className="shrink-0 flex items-center gap-0.5 text-[10px] text-primary hover:text-primary/80 px-1.5 py-0.5 rounded hover:bg-primary/10 transition-colors"
        >
          <Plus className="h-2.5 w-2.5" />
          PM
        </button>
      )}
    </div>
  );
}

export function MoldAlertsSidebar() {
  const { user } = useAuth();
  const [createPMForMoldId, setCreatePMForMoldId] = useState<string | null>(null);

  const { data: molds = [], isLoading } = useQuery({
    queryKey: ['molds', user?.organization_id],
    queryFn: () => fetchMolds(user?.organization_id ?? null),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const inMaintenance = molds.filter(m => m.status === 'in_maintenance');
  const overduePM = molds.filter(m => m.pm_due_status === 'red' && m.status !== 'in_maintenance');
  const dueSoon = molds.filter(m => m.pm_due_status === 'amber' && m.status !== 'in_maintenance');

  const hasAny = inMaintenance.length > 0 || overduePM.length > 0 || dueSoon.length > 0;

  if (isLoading) {
    return (
      <div className="bg-card rounded-lg border border-border/50 p-4">
        <div className="animate-pulse space-y-2">
          {[1, 2, 3].map(i => <div key={i} className="h-8 bg-muted rounded" />)}
        </div>
      </div>
    );
  }

  if (!hasAny) {
    return (
      <div className="bg-card rounded-lg border border-border/50 px-4 py-6 text-center">
        <p className="text-xs font-semibold text-muted-foreground mb-1">Mold Health</p>
        <p className="text-[11px] text-muted-foreground/60">All molds are healthy</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card rounded-lg border border-border/50 overflow-hidden">
        <div className="px-3 py-2.5 border-b border-border/50">
          <h3 className="text-xs font-semibold">Mold Health Alerts</h3>
        </div>

        {inMaintenance.length > 0 && (
          <div>
            <SectionHeader
              icon={<Wrench className="h-3 w-3 text-molten-deep" />}
              label="In Maintenance"
              count={inMaintenance.length}
              color="bg-molten/5"
            />
            <div className="divide-y divide-border/30">
              {inMaintenance.slice(0, 5).map(m => <MoldRow key={m.id} mold={m} />)}
              {inMaintenance.length > 5 && (
                <Link to="/maintenance/molds?status=in_maintenance" className="block px-3 py-1.5 text-[11px] text-primary hover:underline text-center">
                  +{inMaintenance.length - 5} more
                </Link>
              )}
            </div>
          </div>
        )}

        {overduePM.length > 0 && (
          <div className={cn(inMaintenance.length > 0 && 'border-t border-border/50')}>
            <SectionHeader
              icon={<AlertTriangle className="h-3 w-3 text-[hsl(var(--status-red))]" />}
              label="Overdue PM"
              count={overduePM.length}
              color="bg-[hsl(var(--status-red-bg))]"
            />
            <div className="divide-y divide-border/30">
              {overduePM.slice(0, 5).map(m => (
                <MoldRow
                  key={m.id}
                  mold={m}
                  showShotsOverdue
                  onCreatePM={setCreatePMForMoldId}
                />
              ))}
              {overduePM.length > 5 && (
                <Link to="/maintenance/molds?status=overdue" className="block px-3 py-1.5 text-[11px] text-primary hover:underline text-center">
                  +{overduePM.length - 5} more
                </Link>
              )}
            </div>
          </div>
        )}

        {dueSoon.length > 0 && (
          <div className={cn((inMaintenance.length > 0 || overduePM.length > 0) && 'border-t border-border/50')}>
            <SectionHeader
              icon={<Clock className="h-3 w-3 text-[hsl(var(--status-amber))]" />}
              label="PM Due Soon"
              count={dueSoon.length}
              color="bg-[hsl(var(--status-amber-bg))]"
            />
            <div className="divide-y divide-border/30">
              {dueSoon.slice(0, 5).map(m => <MoldRow key={m.id} mold={m} />)}
              {dueSoon.length > 5 && (
                <Link to="/maintenance/molds?status=due_soon" className="block px-3 py-1.5 text-[11px] text-primary hover:underline text-center">
                  +{dueSoon.length - 5} more
                </Link>
              )}
            </div>
          </div>
        )}
      </div>

      {/* PM work order creation dialog — opened from overdue PM rows */}
      <CreateWorkOrderDialog
        open={!!createPMForMoldId}
        onOpenChange={open => { if (!open) setCreatePMForMoldId(null); }}
        preselectedMoldId={createPMForMoldId ?? undefined}
        preselectedType="preventive"
      />
    </>
  );
}
